<script>
import { authMethods, notificationMethods }from "@/state/helpers";
import { required, email } from "vuelidate/lib/validators";
import { errorMessages } from "@/helpers/validations/errorMessages";
import InstitutionalLogosBanner from "@/components/institutional-logos-banner.vue";

export default {
  components: {
    InstitutionalLogosBanner,
  },
  data() {
    return {
      email: "",
      submitted: false,
      error: null,
      tryingToReset: false,
      isResetError: false,
      loading: false,
      errorMsg: null,
      errorMessages,
    };
  },
  validations: {
    email: { required, email }
  },
  created() {
    document.body.classList.add("auth-body-bg");
  },
  methods: {
    ...authMethods,
    ...notificationMethods,
    // Try to register the user in with the email, fullname
    // and password they provided.
    async tryToReset() {
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      } else {
        this.loading = true;
        this.tryingToReset = true;
        // Reset the authError if it existed.
        this.errorMsg = null;
        try {
            await this.forgotPassword({
              email: this.email
            });
            this.loading = false;
            this.tryingToReset = false;
            this.isResetError = false;
            this.success("Hemos mandado un enlace a tu email para restaurar tu contraseña..");
            this.$router.push({ name: "login" });
        }
        catch (error) {
            this.loading = false;            
            let errorMsg = `<div>${error?.response?.data?.extra[0]?.errors?.email[0]}</div>`;
            this.errorMsg = errorMsg;
        }
      }
    }
  }
};
</script>

<template>
  <div class="auth-page-wrapper">
    <!-- Background decorative elements -->
    <div class="auth-background">
      <div class="auth-shape auth-shape-1"></div>
      <div class="auth-shape auth-shape-2"></div>
      <div class="auth-shape auth-shape-3"></div>
    </div>

    <!-- Main content -->
    <div class="auth-container">
      <div class="auth-card">
        <!-- Logo Section -->
        <div class="auth-logo-section">
          <div class="auth-logo-wrapper">
            <img
              src="@/assets/images/logo-CEX.png"
              alt="CEX Logo"
              class="auth-main-logo"
            />
          </div>
          <div class="auth-icon-wrapper">
            <i class="ri-lock-password-line auth-icon-large"></i>
          </div>
          <h1 class="auth-welcome-title">¿Olvidaste tu contraseña?</h1>
          <p class="auth-welcome-subtitle">
            No te preocupes, te enviaremos un enlace para restablecerla
          </p>
        </div>

        <!-- Alert Messages -->
        <b-alert variant="danger" class="auth-alert" v-if="errorMsg" show dismissible>
          <div v-html="errorMsg"></div>
        </b-alert>

        <!-- Reset Form -->
        <form class="auth-form" @submit.prevent="tryToReset">
          <!-- Email Field -->
          <div class="auth-form-group">
            <label for="useremail" class="auth-label">
              <i class="ri-mail-line auth-label-icon"></i>
              Correo electrónico
            </label>
            <div class="auth-input-wrapper">
              <input
                v-model="email"
                type="email"
                class="auth-input"
                id="useremail"
                placeholder="tu@email.com"
                :class="{ 'auth-input-error': submitted && $v.email.$error }"
              />
              <div v-if="submitted && $v.email.$error" class="auth-error-message">
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.email.required">{{ errorMessages.required }}</span>
                <span v-if="!$v.email.email">{{ errorMessages.email }}</span>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <button
            class="auth-submit-btn"
            type="submit"
            :disabled="loading"
          >
            <span v-if="loading" class="auth-spinner"></span>
            <span v-else>
              <i class="ri-mail-send-line"></i>
              Enviar enlace de recuperación
            </span>
          </button>
        </form>

        <!-- Back to Login Link -->
        <div class="auth-footer">
          <router-link
            tag="a"
            to="/login"
            class="auth-link auth-link-back"
          >
            <i class="ri-arrow-left-line"></i>
            Volver al inicio de sesión
          </router-link>
        </div>
      </div>

      <!-- Decorative side panel -->
      <div class="auth-side-panel">
        <div class="auth-side-content">
          <h2 class="auth-side-title">Recupera tu acceso</h2>
          <p class="auth-side-description">
            Te ayudaremos a recuperar el acceso a tu cuenta de forma segura
          </p>
          <div class="auth-side-features">
            <div class="auth-feature-item">
              <i class="ri-shield-star-line"></i>
              <span>Proceso seguro</span>
            </div>
            <div class="auth-feature-item">
              <i class="ri-time-line"></i>
              <span>Enlace válido por 24 horas</span>
            </div>
            <div class="auth-feature-item">
              <i class="ri-mail-check-line"></i>
              <span>Revisa tu bandeja de entrada</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Institutional Logos Banner -->
    <div class="auth-logos-banner-wrapper">
      <InstitutionalLogosBanner variant="default" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.auth-icon-wrapper {
  margin: 1.5rem 0;
  display: flex;
  justify-content: center;
}

.auth-icon-large {
  font-size: 4rem;
  color: #6ea637;
  opacity: 0.8;
}

.auth-link-back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}
</style>
